import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Skeleton } from '../../../components/ui/skeleton';
import { ScrollArea } from '../../../components/ui/scroll-area';

import { 
  Search, 
  Filter, 
  Download, 
  Pause, 
  Play,
  AlertCircle,
  Info,
  AlertTriangle,
  XCircle,
  Clock,
  Terminal
} from 'lucide-react';

/**
 * Componente de monitoreo de logs en tiempo real
 * Muestra logs de la aplicación con filtros y búsqueda
 */
const LogsMonitor = ({ logs, isLoading, autoRefresh, onRefresh }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [isPaused, setIsPaused] = useState(false);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const scrollAreaRef = useRef(null);

  // Filtrar logs
  useEffect(() => {
    if (!logs) return;

    const filtered = logs.filter(log => {
      const matchesSearch = !searchTerm || 
        log.message?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.source?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesLevel = levelFilter === 'all' || log.level === levelFilter;
      const matchesSource = sourceFilter === 'all' || log.source === sourceFilter;
      
      return matchesSearch && matchesLevel && matchesSource;
    });

    setFilteredLogs(filtered);
  }, [logs, searchTerm, levelFilter, sourceFilter]);

  // Auto-scroll al final cuando hay nuevos logs (solo si no está pausado)
  useEffect(() => {
    if (!isPaused && scrollAreaRef.current && filteredLogs.length > 0) {
      const scrollElement = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollElement) {
        scrollElement.scrollTop = scrollElement.scrollHeight;
      }
    }
  }, [filteredLogs, isPaused]);

  // Obtener icono según el nivel del log
  const getLevelIcon = (level) => {
    switch (level) {
      case 'error':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'warn':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'info':
        return <Info className="h-4 w-4 text-blue-500" />;
      case 'trace':
        return <Terminal className="h-4 w-4 text-gray-500" />;
      default:
        return <Info className="h-4 w-4 text-gray-500" />;
    }
  };

  // Obtener badge según el nivel del log
  const getLevelBadge = (level) => {
    const variants = {
      error: 'destructive',
      warn: 'secondary',
      info: 'default',
      trace: 'outline'
    };

    return (
      <Badge variant={variants[level] || 'outline'} className="text-xs">
        {level?.toUpperCase() || 'LOG'}
      </Badge>
    );
  };

  // Formatear timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    
    const date = new Date(timestamp);
    return date.toLocaleTimeString();
  };

  // Obtener fuentes únicas para el filtro
  const getUniqueSources = () => {
    if (!logs) return [];
    
    const sources = [...new Set(logs.map(log => log.source).filter(Boolean))];
    return sources.sort();
  };

  // Exportar logs
  const exportLogs = () => {
    const logsText = filteredLogs.map(log => 
      `[${formatTimestamp(log.timestamp)}] ${log.level?.toUpperCase() || 'LOG'} ${log.source || 'unknown'}: ${log.message || ''}`
    ).join('\n');

    const blob = new Blob([logsText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `logs-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="flex items-center space-x-2">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 flex-1" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center space-x-2">
              <Terminal className="h-5 w-5" />
              <span>Logs Monitor</span>
              {autoRefresh && !isPaused && (
                <Badge variant="secondary" className="ml-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-1" />
                  Live
                </Badge>
              )}
            </CardTitle>
            <CardDescription>
              Monitoreo en tiempo real de logs de la aplicación
            </CardDescription>
          </div>

          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsPaused(!isPaused)}
            >
              {isPaused ? (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  Resume
                </>
              ) : (
                <>
                  <Pause className="h-4 w-4 mr-2" />
                  Pause
                </>
              )}
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={exportLogs}
              disabled={filteredLogs.length === 0}
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={onRefresh}
            >
              Refresh
            </Button>
          </div>
        </div>

        {/* Filtros */}
        <div className="flex items-center space-x-4 mt-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar en logs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            
            <select
              value={levelFilter}
              onChange={(e) => setLevelFilter(e.target.value)}
              className="px-3 py-2 border rounded-md text-sm"
            >
              <option value="all">Todos los niveles</option>
              <option value="error">Error</option>
              <option value="warn">Warning</option>
              <option value="info">Info</option>
              <option value="trace">Trace</option>
            </select>

            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="px-3 py-2 border rounded-md text-sm"
            >
              <option value="all">Todas las fuentes</option>
              {getUniqueSources().map(source => (
                <option key={source} value={source}>{source}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Estadísticas */}
        <div className="flex items-center space-x-4 mt-4 text-sm text-muted-foreground">
          <span>Total: {logs?.length || 0}</span>
          <span>Filtrados: {filteredLogs.length}</span>
          {logs && (
            <>
              <span className="text-[var(--yool-error)]">
                Errors: {logs.filter(l => l.level === 'error').length}
              </span>
              <span className="text-yellow-600">
                Warnings: {logs.filter(l => l.level === 'warn').length}
              </span>
            </>
          )}
        </div>
      </CardHeader>

      <CardContent>
        <ScrollArea className="h-96 w-full border rounded-md p-4" ref={scrollAreaRef}>
          {filteredLogs.length > 0 ? (
            <div className="space-y-2 font-mono text-sm">
              {filteredLogs.map((log, index) => (
                <div 
                  key={index} 
                  className="flex items-start space-x-3 p-2 hover:bg-muted/50 rounded-sm transition-colors"
                >
                  {/* Timestamp */}
                  <div className="flex items-center space-x-2 text-muted-foreground min-w-0">
                    <Clock className="h-3 w-3" />
                    <span className="text-xs">
                      {formatTimestamp(log.timestamp)}
                    </span>
                  </div>

                  {/* Level */}
                  <div className="flex items-center space-x-1">
                    {getLevelIcon(log.level)}
                    {getLevelBadge(log.level)}
                  </div>

                  {/* Source */}
                  {log.source && (
                    <Badge variant="outline" className="text-xs">
                      {log.source}
                    </Badge>
                  )}

                  {/* Message */}
                  <div className="flex-1 min-w-0">
                    <p className="break-words">
                      {log.message || 'No message'}
                    </p>
                    
                    {/* Metadata adicional */}
                    {log.metadata && (
                      <div className="mt-1 text-xs text-muted-foreground">
                        {Object.entries(log.metadata).map(([key, value]) => (
                          <span key={key} className="mr-3">
                            {key}: {JSON.stringify(value)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-32 text-muted-foreground">
              {searchTerm || levelFilter !== 'all' || sourceFilter !== 'all' 
                ? 'No se encontraron logs con los filtros aplicados'
                : 'No hay logs disponibles'
              }
            </div>
          )}
        </ScrollArea>

        {/* Indicador de pausa */}
        {isPaused && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <div className="flex items-center space-x-2 text-yellow-800">
              <Pause className="h-4 w-4" />
              <span className="text-sm font-medium">
                Monitoreo pausado - Los nuevos logs no se mostrarán automáticamente
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default LogsMonitor;